import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { SessionGrant } from "../../types/session";
import { rendererPreview } from "../preview";
import type { PreviewFactory } from "../preview";
import { detectDevice } from "./device";
import type { DeviceSupport } from "./device";

/**
 * FS-01 부트스트랩 의존성 — 테스트·가짜 모드에서 갈아 끼운다.
 * S1 에서는 자산 캐시·스냅샷 목록 API 가 아직 없어 자리표시(즉시 완료)다:
 *  - loadAssets: S2 자산 매니페스트(@picnic-field/assets) 캐시
 *  - loadWorks: S2 GET /snapshots?course=… (학생: 내 기계·친구 작품 / 교사: 반 작품)
 */
export interface BootDeps {
  detectDevice: () => DeviceSupport;
  loadAssets: (s: SessionGrant) => Promise<void>;
  loadMyMachine: (s: SessionGrant) => Promise<void>;
  loadWorks: (s: SessionGrant) => Promise<void>;
  /** FS-C1-02 '캠퍼스로' — 기본: VITE_CAMPUS_URL 또는 이전 페이지 */
  backToCampus: () => void;
  /** 개발 전용(가짜 API) — 입장 실패 화면 아래에 가짜 런치 링크를 보인다 */
  devPanel?: ReactNode;
  /** 개발 전용(가짜 API) — 화면 구석의 개발 도크(SE/SU 전환 등). 프로덕션·E2E 화면에는 없다 */
  devDock?: ReactNode;
  /** 휴식 안내 간격(기본 30분 · SR-SEC-05) — 가짜 모드는 ?break=<ms> 로 줄인다 */
  breakAfterMs?: number;
  /** 3D 미리보기(C-02) — 기본은 렌더러 지연 로드, 테스트는 가짜 */
  createPreview: PreviewFactory;
}

export function defaultBootDeps(campusUrl: string | null): BootDeps {
  const done = () => Promise.resolve();
  return {
    detectDevice,
    loadAssets: done,
    loadMyMachine: done,
    loadWorks: done,
    createPreview: rendererPreview,
    backToCampus: () => {
      if (campusUrl) window.location.assign(campusUrl);
      else if (window.history.length > 1) window.history.back();
      else window.close();
    },
  };
}

const Ctx = createContext<BootDeps | null>(null);

export function BootDepsProvider({ deps, children }: { deps: BootDeps; children: ReactNode }) {
  return <Ctx.Provider value={deps}>{children}</Ctx.Provider>;
}

export function useBootDeps(): BootDeps {
  const v = useContext(Ctx);
  if (!v) throw new Error("BootDepsProvider 밖입니다");
  return v;
}
